'use client'

import Script from 'next/script'
import { usePathname } from 'next/navigation'

/**
 * Searchlark Journeys: cookieless visitor analytics (page views, scroll depth,
 * link and button clicks, form starts and sends; never what is typed). It
 * stores nothing in the browser and honours Global Privacy Control / Do Not
 * Track. Rendered once, from the root layout, on the production host only.
 */
export const SEARCHLARK_SRC = 'https://searchlark.com/j.js'
export const SEARCHLARK_KEY = 'jk_cc0399defb84f2d2f9f01123'

/**
 * The tag never loads here: the admin area, and pages opened from emailed
 * links whose URL carries a token (reference forms, marketing consent,
 * password recovery).
 */
export const SEARCHLARK_NO_LOAD_PREFIXES = ['/admin', '/verify', '/consent', '/auth/update-password']

/**
 * Loaded, but no titles or link text are recorded: the signed-in areas, plus
 * the pages that list or show au pairs by name (/aupair/<id> profiles and
 * /family/search). Goals still count. The no-load prefixes are repeated so a
 * client-side navigation into them records nothing either.
 */
export const SEARCHLARK_CONTENT_OFF = [
  '/aupair',
  '/family',
  '/partners',
  '/messages',
  '/settings',
  '/onboarding',
  '/review',
  '/welcome-back',
  ...SEARCHLARK_NO_LOAD_PREFIXES,
]

export default function SearchlarkJourneys({ nonce }: { nonce?: string }) {
  const pathname = usePathname() ?? '/'
  if (SEARCHLARK_NO_LOAD_PREFIXES.some((p) => pathname.startsWith(p))) return null
  // Personal data on public pages (au pair cards) carries data-sl-mask.
  return (
    <Script
      id="searchlark-journeys"
      src={SEARCHLARK_SRC}
      strategy="afterInteractive"
      nonce={nonce}
      data-key={SEARCHLARK_KEY}
      data-content-off={SEARCHLARK_CONTENT_OFF.join(',')}
      // Masked session replays (Searchlark Journeys replay): text masked, inputs never recorded,
      // au pair cards (data-sl-mask) blocked and nothing recorded on the content-off paths.
      data-replay="100"
    />
  )
}

type SearchlarkFn = ((command: 'goal', name: string) => void) & { q?: unknown[] }

declare global {
  interface Window {
    searchlark?: SearchlarkFn
  }
}

/**
 * Reports a goal once the action has succeeded. A call made before j.js has
 * loaded (for example right after a redirect to a success page) goes into
 * Searchlark's documented queue stub, which j.js reads when it loads. Never
 * throws.
 */
export function searchlarkGoal(name: string): void {
  try {
    if (!window.searchlark) {
      const stub: SearchlarkFn = function (...args: unknown[]) {
        (stub.q = stub.q || []).push(args)
      }
      window.searchlark = stub
    }
    window.searchlark('goal', name)
  } catch {
    // Analytics must never break the page.
  }
}
